import axios from 'axios';

const MAX_DIMENSION = 1600;  // px — enough for option cards, keeps phone photos small
const JPEG_QUALITY  = 0.85;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // API RequestSizeLimit (10 MB)

/**
 * Downscale an image (e.g. a 12MP phone photo) to at most MAX_DIMENSION on the long side
 * and re-encode as JPEG. EXIF orientation is applied by the browser when decoding.
 * Throws if the browser cannot decode the file (e.g. HEIC on desktop Chrome).
 */
export async function resizeImage(file: File): Promise<File> {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('This image format is not supported. Please use JPG or PNG.'));
            el.src = url;
        });

        const scale  = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
        const width  = Math.round(img.naturalWidth * scale);
        const height = Math.round(img.naturalHeight * scale);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Unable to process image.');
        ctx.fillStyle = '#fff'; // flatten PNG transparency onto white for JPEG
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        if (!blob) throw new Error('Unable to process image.');

        const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
        return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** Resize then upload an image to Azure Blob via the API. Returns a readable (SAS) URL. */
export async function uploadImage(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) throw new Error('Please select an image file.');

    const resized = await resizeImage(file);
    if (resized.size > MAX_UPLOAD_BYTES) throw new Error('Image is too large (max 10 MB).');

    const formData = new FormData();
    formData.append('file', resized);
    try {
        const res = await axios.post<{ path: string }>(
            `${process.env.REACT_APP_API_END_POINT}/v1/Upload/UploadImage`,
            formData,
            { headers: { Authorization: 'Bearer ' + sessionStorage.getItem('accessToken'), 'Content-Type': 'multipart/form-data' } },
        );
        if (!res.data?.path) throw new Error('Upload failed.');
        return res.data.path;
    } catch (err: any) {
        throw new Error(err?.response?.data?.message || err?.message || 'Upload failed.');
    }
}
